import PlanReportsPage from "./PlanReportsPage";
import UpsalesSyncPage from "./UpsalesSyncPage";
import { Card, Chip, Stack, Typography } from "@mui/material";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { translateKey } from "../i18n/translate";
import { PageContainer, PageHeader } from "../components/PageHeader";
import VodTitlesPage from "./VodTitlesPage";
import CarouselsPage from "./CarouselsPage";
import ChannelsPage from "./ChannelsPage";
import ChannelLineupPage from "./ChannelLineupPage";
import ContentPackagesPage from "./ContentPackagesPage";
import ContentProvidersPage from "./ContentProvidersPage";
import ContentProductsPage from "./ContentProductsPage";
import SubmissionsPage from "./SubmissionsPage";
import BulkIngestPage from "./BulkIngestPage";
import IngestOverviewPage from "./IngestOverviewPage";
import OrderSupportPage from "./OrderSupportPage";
import PortalCustomersPage from "./PortalCustomersPage";

/**
 * Distribution - the platform-level content pipeline.
 *
 * Everything under a product area answers "what does THIS place show?".
 * Distribution answers the question one level up: what is there to show at all,
 * and how does it get onto the platform. Channels are the broadcast line-up,
 * VOD is the on-demand shelf, and bulk ingest is the pipe that fills both.
 *
 * None of it is scoped to a location, so it lives in the Admin block beside the
 * other Nenda-side catalogues rather than inside a product.
 *
 * Channels and VOD are real surfaces over the Nenda Content API (`BE-15`), and
 * bulk ingest is real too since `BE-31` — a package of many titles over the
 * single-title lifecycle `BE-17` shipped.
 *
 * The placeholder below is kept because the shape it holds is still the right
 * one for the next section that exists in the design and not yet in the app.
 */

interface DistributionSection {
  key: string;
  /** What this page will do once it is built - written now, while it is fresh. */
  intentKeys: string[];
}

const SECTIONS: DistributionSection[] = [
  { key: "channels", intentKeys: [] },
  { key: "lineup", intentKeys: [] },
  {
    key: "vod",
    intentKeys: [
      "distributionPage.sections.vod.intent_1",
      "distributionPage.sections.vod.intent_2",
      "distributionPage.sections.vod.intent_3",
    ],
  },
  { key: "carousels", intentKeys: [] },
  { key: "products", intentKeys: [] },
  { key: "packages", intentKeys: [] },
  { key: "providers", intentKeys: [] },
  { key: "submissions", intentKeys: [] },
  { key: "ingest", intentKeys: [] },
  { key: "bulk-ingest", intentKeys: [] },
  { key: "order-support", intentKeys: [] },
  { key: "portal-customers", intentKeys: [] },
  { key: "plan-reports", intentKeys: [] },
  { key: "upsales", intentKeys: [] },
];

/**
 * Sections that used to exist and now redirect.
 *
 * "Effect channels" and "Live channels" were two menu entries. Live channels
 * became the Channels list; EFFECT channels went away entirely - an effect
 * channel is a loop you build, and Foyer already has a first-class model for
 * that in `SignagePlaylist` (`BE-3`, superseded). Both names were in the menu,
 * so both are in somebody's bookmarks - a 404 for them would be a
 * self-inflicted one, and landing on Channels is the closest true answer.
 */
const MERGED_SECTIONS: Record<string, string> = {
  "effect-channels": "channels",
  "live-channels": "channels",
};

export default function DistributionPage() {
  const { t } = useTranslation("content");
  const { section } = useParams<{ section: string }>();
  const merged = section ? MERGED_SECTIONS[section] : undefined;
  const match = SECTIONS.find((s) => s.key === section);

  if (merged) return <Navigate to={`/distribution/${merged}`} replace />;
  // An unknown section is a stale bookmark, not an error worth a page.
  if (!match) return <Navigate to="/distribution/channels" replace />;

  // The sections with a real surface behind them. The rest fall through to the
  // placeholder below.
  if (match.key === "channels") return <ChannelsPage />;
  if (match.key === "lineup") return <ChannelLineupPage />;
  if (match.key === "vod") return <VodTitlesPage />;
  if (match.key === "carousels") return <CarouselsPage />;
  if (match.key === "products") return <ContentProductsPage />;
  if (match.key === "packages") return <ContentPackagesPage />;
  if (match.key === "providers") return <ContentProvidersPage />;
  if (match.key === "submissions") return <SubmissionsPage />;
  if (match.key === "bulk-ingest") return <BulkIngestPage />;
  if (match.key === "ingest") return <IngestOverviewPage />;
  if (match.key === "order-support") return <OrderSupportPage />;
  if (match.key === "portal-customers") return <PortalCustomersPage />;
  if (match.key === "upsales") return <UpsalesSyncPage />;
  if (match.key === "plan-reports") return <PlanReportsPage />;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("distributionPage.eyebrow")}
        title={translateKey(t, `distributionPage.sections.${match.key}.title`)}
        subtitle={translateKey(
          t,
          `distributionPage.sections.${match.key}.summary`
        )}
      />
      <Card variant="outlined" sx={{ p: 4 }}>
        <Stack spacing={2} alignItems="flex-start">
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={t("distributionPage.notBuilt")}
          />
          <Typography variant="body2" color="text.secondary">
            {t("distributionPage.placeholderIntro")}
          </Typography>
          <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
            {match.intentKeys.map((key) => (
              <Typography
                key={key}
                component="li"
                variant="body2"
                color="text.secondary"
              >
                {translateKey(t, key)}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Card>
    </PageContainer>
  );
}
